import { AlertTriangle } from "lucide-react";
import { useEffect, useState } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { isApiError, useResetProjectDbPassword } from "@/lib/api";
import { logger } from "@/lib/logger";

interface ProjectResetDbPasswordDialogProps {
  projectId: number;
  projectName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Confirmation dialog for the self-service Supabase DB password reset
 * (BOIE-134). Rotates the project's Postgres password and pushes the new
 * value to the repo's `SUPABASE_DB_PASSWORD` CI secret. Existing direct DB
 * connections using the old password break immediately; the new password is
 * only stored in the CI secret, so for direct access it must be retrieved
 * from the Supabase Dashboard.
 */
export function ProjectResetDbPasswordDialog({
  projectId,
  projectName,
  open,
  onOpenChange,
}: ProjectResetDbPasswordDialogProps) {
  const resetDbPassword = useResetProjectDbPassword(projectId);
  const [error, setError] = useState("");
  const [result, setResult] = useState<{ message: string; secretUpdated: boolean } | null>(null);
  const sending = resetDbPassword.isPending;

  // Reset transient state whenever the dialog opens for a (new) project.
  useEffect(() => {
    if (open) {
      setError("");
      setResult(null);
    }
  }, [open, projectId]);

  const handleReset = async () => {
    setError("");
    try {
      const data = await resetDbPassword.mutateAsync();
      setResult({
        message: data?.message ?? "Database password reset.",
        secretUpdated: data?.secret_updated ?? false,
      });
    } catch (err) {
      logger.error("[project-reset-db-password] failed:", err);
      const detail =
        isApiError(err) && err.body && typeof err.body === "object" && "detail" in err.body
          ? String((err.body as Record<string, unknown>).detail)
          : "Failed to reset database password. Please try again.";
      setError(detail);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Reset database password</AlertDialogTitle>
          <AlertDialogDescription>
            {result ? (
              result.message
            ) : (
              <>
                This rotates <strong>{projectName}</strong>&apos;s Supabase Postgres password and
                automatically updates the repository&apos;s{" "}
                <code className="font-mono">SUPABASE_DB_PASSWORD</code> CI secret. Any direct
                database connections using the old password (psql, other tooling) will stop working
                immediately. The new password isn&apos;t shown here &mdash; if you need it for
                direct access, retrieve or reset it from the Supabase Dashboard.
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {result && !result.secretUpdated && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-500 dark:bg-amber-950">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
            <p className="text-sm text-amber-800 dark:font-medium dark:text-amber-200">
              The password was rotated, but the GitHub{" "}
              <code className="font-mono">SUPABASE_DB_PASSWORD</code> secret could not be updated.
              CI migrations will fail until the secret is updated manually or the reset is retried.
            </p>
          </div>
        )}

        {error && <p className="text-destructive text-sm">{error}</p>}

        <AlertDialogFooter>
          {result ? (
            <AlertDialogAction onClick={() => onOpenChange(false)}>Close</AlertDialogAction>
          ) : (
            <>
              <AlertDialogCancel disabled={sending}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  handleReset();
                }}
                disabled={sending}
              >
                {sending ? "Resetting..." : "Reset password"}
              </AlertDialogAction>
            </>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
